import { Box, Theme, Typography, useTheme } from '@mui/material';

interface ColorSet {
  background: string;
  text: string;
}

interface ProgressBarProps {
  progress: number;
  leftLabel: string;
  rightLabel: string;
  primaryColor?: ColorSet;
  secondaryColor?: ColorSet;
}

const useStyles = (theme: Theme) => ({
  container: {
    display: 'flex',
    width: '100%',
    overflow: 'hidden',
    borderRadius: '32px',
    border: `3px solid ${theme.palette.border.main}`,
  },
  baseSegment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    py: 1,
    transition: 'flex 0.5s ease',
  },
  fullSegment: {
    flex: 1,
    borderRadius: '32px',
  },
  leftSegment: {
    borderTopLeftRadius: '32px',
    borderBottomLeftRadius: '32px',
  },
  rightSegment: {
    borderTopRightRadius: '32px',
    borderBottomRightRadius: '32px',
  },
});

export const ProgressBar = ({ progress, leftLabel, rightLabel, primaryColor, secondaryColor }: ProgressBarProps) => {
  const isContinuous = progress >= 100;
  const isStop = progress === 0;
  const theme = useTheme();
  const styles = useStyles(theme);

  if (isContinuous) {
    return (
      <Box sx={styles.container}>
        <Box sx={{ ...styles.baseSegment, ...styles.fullSegment, backgroundColor: primaryColor?.background }}>
          <Typography fontWeight='bold' sx={{ color: primaryColor?.text }}>
            {leftLabel}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isStop) {
    return (
      <Box sx={styles.container}>
        <Box sx={{ ...styles.baseSegment, ...styles.fullSegment, backgroundColor: secondaryColor?.background }}>
          <Typography fontWeight='bold' sx={{ color: secondaryColor?.text }}>
            {rightLabel}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.baseSegment,
          ...styles.leftSegment,
          flex: progress,
          backgroundColor: primaryColor?.background,
        }}>
        <Typography fontWeight='bold' sx={{ color: primaryColor?.text }}>
          {leftLabel}
        </Typography>
      </Box>
      <Box
        sx={{
          ...styles.baseSegment,
          ...styles.rightSegment,
          flex: 100 - progress,
          backgroundColor: secondaryColor?.background,
        }}>
        <Typography fontWeight='bold' sx={{ color: secondaryColor?.text }}>
          {rightLabel}
        </Typography>
      </Box>
    </Box>
  );
};
